import { app } from "js/namespaces.js";
import { _ } from "js/vendor";
import * as geom from "js/core/utilities/geom";
import { CollectionElement, CollectionItemElement } from "js/elements/base/CollectionElement";
import { ContentElement } from "js/elements/base/ContentElement";
import { BoxFrame, ImageFrame } from "js/elements/base/Frame";
import { linearPartition } from "js/core/utilities/linearPartition";
import { tileImages } from "js/core/utilities/imageTiling";
import {
    BackgroundStyleType,
    ElementTextBlockPositionType,
    PaletteColorType,
    PositionType,
    ContentBlockType,
    TextBlockPreset
} from "common/constants";
import { migrateTextModel, TextElement } from "js/elements/base/TextElement";
import { ContentBlockContainer } from "./ContentBlock";
import { PositionableTextElement } from "../base/PositionableTextElement";
import { getValueOrDefault } from "../../core/utilities/extensions";

class PhotoCollage extends CollectionElement {
    getChildItemType(itemModel) {
        return PhotoCollageItem;
    }

    // get canSelect() {
    //     return false;
    // }

    get fullBleed() {
        if (this.aspectRatio == "fit") { // || this.canvas.model.layout.elementTextBlockPosition == ElementTextBlockPositionType.INLINE) {
            return false;
        } else {
            return this.options.fullBleed || this.model.fullBleed;
        }
    }

    get showCaption() {
        return this.model.showCaption;
    }

    get aspectRatio() {
        return this.model.aspectRatio || "fill";
    }

    get allowText() {
        return true;
    }

    get allowImage() {
        return true;
    }

    get allowFrame() {
        return true;
    }

    get showFrame() {
        return this.model.showFrame || false;
    }

    get frame() {
        return this.model.frame || "none";
    }

    get showGutter() {
        if (this.itemElements.length > 1 || (this.fullBleed)) { // && (!isTrayContainer || !element.canvas.model.layout.trayLayout.contains("inline"))
            return this.model.showGutter || false;
        } else {
            return false;
        }
    }

    get reserveFooterSpace() {
        if (this.fullBleed && this.showGutter) {
            return true;
        } else {
            return false;
        }
    }

    get showShadow() {
        return this.frame == "light";
    }

    get gutterSize() {
        return 10;
    }

    get layoutType() {
        return this.model.layoutType || "grid";
    }

    getCanvasMargins() {
        if (this.fullBleed) {
            let margins;
            if (this.showGutter) {
                margins = {
                    left: this.gutterSize,
                    right: this.gutterSize,
                    top: this.gutterSize,
                    bottom: this.gutterSize
                };
            } else {
                margins = { left: 0, right: 0, top: 0, bottom: 0 };
            }

            if (this.canvas.model.layout.elementTextBlockPosition == ElementTextBlockPositionType.INLINE) {
                margins.bottom = 50;
            }

            return margins;
        } else {
            return super.getCanvasMargins();
        }
    }

    _loadStyles(styles) {
        if (this.showShadow && this.showGutter == false && this.aspectRatio != "fit") {
            // if no gutter, we need to add the shadow to the entire collage so we don't get overlap in the seams
            // otherwise we assign shadow to each item
            styles.shadow = {
                blur: 14,
                offsetX: 0,
                offsetY: 0,
                opacity: 0.2
            };
        }
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let showGutter = options.showGutter != undefined ? options.showGutter : this.showGutter;
        let layoutType = options.layoutType ? options.layoutType : this.layoutType;
        let fitAspectRatio = options.aspectRatio ? options.aspectRatio == "fit" : this.aspectRatio == "fit";

        let availableSize = size.clone();

        if (showGutter) {
            this.gap = this.gutterSize;
        } else if (this.frame != "none") {
            this.gap = -app.currentTheme.styleSheet.decorationVariables[app.currentTheme.styleSheet.BoxFrame.strokeWidth.substr(1)];
        } else {
            this.gap = 0;
        }

        let calculatedSize;
        switch (layoutType) {
            case "vertical":
                if (fitAspectRatio) {
                    calculatedSize = this.layoutVerticalFit(availableSize);
                } else {
                    calculatedSize = this.layoutVerticalFilled(availableSize);
                }
                break;
            case "horizontal":
                if (fitAspectRatio) {
                    calculatedSize = this.layoutHorizontalFit(availableSize);
                } else {
                    calculatedSize = this.layoutHorizontalFilled(availableSize);
                }
                break;
            case "grid":
            default:
                if (fitAspectRatio) {
                    calculatedSize = this.layoutTiles(props, availableSize);
                } else {
                    calculatedSize = this.layoutGrid(props, availableSize);
                }
                break;
        }

        if (this.model.matchTextScale) {
            // if any headline textscales differ, recalc all the cells with a forceTextScale option to make them all the same
            let textScales = _.map(this.itemElements, cell => cell.contentBlocks ? cell.contentBlocks.calculatedProps.scale : null);
            if (_.uniq(textScales).length > 1) {
                let minTextScale = _.min(textScales);
                for (let cell of this.itemElements) {
                    cell.calcProps(cell.calculatedProps.size, { forceTextScale: minTextScale });
                }
            }
        }

        props.isFit = this.itemCount <= (this.styles.maxItemCount || 5);

        return { size: calculatedSize };
    }

    // region Layouters
    layoutVertical(size) {
        let availableItemHeight = size.height - (this.gap * (this.itemElements.length - 1));
        let itemSizes = _.map(this.itemElements, item => new geom.Size(1, 1 / item.contentSize.aspectRatio));
        let width = availableItemHeight / _.sumBy(itemSizes, item => item.height);
        width = Math.min(size.width, width);

        let totalHeight = -this.gap;
        for (let item of itemSizes) {
            item.width = width;
            item.height *= width;
            totalHeight += item.height + this.gap;
        }

        let x = size.width / 2 - width / 2;
        let y = size.height / 2 - totalHeight / 2;

        this.itemElements.forEach((item, index) => {
            let itemSize = itemSizes[index];
            item.calcSize(itemSize);
            item.bounds = new geom.Rect(x, y, itemSize);
            y += itemSize.height + this.gap;
        });

        return new geom.Size(width, totalHeight);
    }

    layoutHorizontalFilled(size) {
        let itemWidth = (size.width - this.gap * (this.itemElements.length - 1)) / this.itemElements.length;

        let x = 0;
        for (let item of this.itemElements) {
            item.calcSize(new geom.Size(itemWidth, size.height));
            item.bounds = new geom.Rect(x, 0, itemWidth, size.height);
            x += itemWidth + this.gap;
        }

        return { size };
    }

    layoutVerticalFilled(size) {
        let itemHeight = (size.height - this.gap * (this.itemElements.length - 1)) / this.itemElements.length;
        let y = 0;
        for (let item of this.itemElements) {
            item.calcSize(new geom.Size(size.width, itemHeight));
            item.bounds = new geom.Rect(0, y, size.width, itemHeight);
            y += itemHeight + this.gap;
        }

        return { size };
    }

    layoutVerticalFit(size) {
        let availableItemHeight = size.height - (this.gap * (this.itemElements.length - 1));
        let itemSizes = _.map(this.itemElements, item => new geom.Size(1, 1 / item.contentSize.aspectRatio));
        let width = availableItemHeight / _.sumBy(itemSizes, item => item.height);

        width = Math.min(size.width, width);

        let totalHeight = -this.gap;
        for (let item of itemSizes) {
            item.width = width;
            item.height *= width;
            totalHeight += item.height + this.gap;
        }

        let x = size.width / 2 - width / 2;
        let y = size.height / 2 - totalHeight / 2;

        this.itemElements.forEach((item, index) => {
            let itemSize = itemSizes[index];
            item.calcSize(itemSize);
            item.bounds = new geom.Rect(x, y, itemSize);
            y += itemSize.height + this.gap;
        });

        return new geom.Size(width, totalHeight);
    }

    layoutHorizontalFit(size) {
        let availableTotalWidth = size.width - (this.gap * (this.itemElements.length - 1));
        let itemSizes = _.map(this.itemElements, item => new geom.Size(item.contentSize.aspectRatio, 1));
        let normalizedTotalWidth = _.sumBy(itemSizes, item => item.width);
        let height = availableTotalWidth / normalizedTotalWidth;
        height = Math.min(size.height, height);

        let totalWidth = -this.gap;
        for (let item of itemSizes) {
            item.height = height;
            item.width *= height;
            totalWidth += item.width + this.gap;
        }

        let x = size.width / 2 - totalWidth / 2;
        let y = size.height / 2 - height / 2;

        this.itemElements.forEach((item, index) => {
            let itemSize = itemSizes[index];
            item.calcSize(itemSize);
            item.bounds = new geom.Rect(x, y, itemSize);
            x += itemSize.width + this.gap;
        });
        return new geom.Size(totalWidth, height);
    }

    // layoutOverlapping2(size) {
    //     let maxPerRow = 4;
    //     let overlap = this.element.model.has("overlap") ? this.element.model.get("overlap") : 0;
    //     let maxRotate = this.element.model.has("maxRotate") ? this.element.model.get("maxRotate") : 12;
    //     let verticalAltSpacing = 20;
    //
    //
    //     var seed = 2;
    //
    //     function random() {
    //         var x = Math.sin(seed++) * 10000;
    //         return x - Math.floor(x);
    //     }
    //
    //
    //     let origContentSizes = [];
    //     for (let item of this.itemElements) {
    //         origContentSizes.push(item.contentSize);
    //     }
    //
    //
    //     let targetRatio = size.width / size.height;
    //
    //     // console.log("target", targetRatio);
    //
    //     let bestScore;
    //     let bestColCount;
    //
    //     let bestSizes;
    //     let bestRowWidths;
    //
    //     let fitPhotos = (count, colCount) => {
    //         let rowCount = Math.ceil(count / colCount);
    //
    //         // console.log("try col=" +  colCount + " rows=" + rowCount);
    //
    //         let rowWidths = [];
    //         let rowSizes = [];
    //
    //         // let minHeight = _.minBy(this.itemElements, item => item.height);
    //         let rowHeight = 1000;
    //
    //         for (let row = 0; row < rowCount; row++) {
    //             let rowWidth = 0;
    //             let items = origContentSizes.slice(row * colCount, row * colCount + colCount);
    //
    //
    //             for (let col = 0; col < items.length; col++) {
    //                 let size = items[col];
    //                 size = size.scale(rowHeight / size.height);
    //                 rowWidth += size.width;
    //                 rowSizes.push(size);
    //             }
    //             rowWidths.push(rowWidth);
    //         }
    //
    //         let maxRowWidth = _.max(rowWidths);
    //
    //         let tryRatio = maxRowWidth / (rowHeight * rowCount);
    //         let score = Math.abs(tryRatio - targetRatio);
    //
    //         // console.log("score", score, tryRatio, maxRowWidth, rowHeight * rowCount);
    //
    //         if (!bestScore || score < bestScore) {
    //             bestScore = score;
    //             bestColCount = colCount;
    //             bestSizes = rowSizes;
    //             bestRowWidths = rowWidths;
    //         }
    //     }
    //
    //
    //     if (this.itemElements.length > 4) {
    //         for (let cols = 1; cols <= this.itemElements.length; cols++) {
    //             fitPhotos(this.itemElements.length, cols);
    //         }
    //     } else if (this.itemElements.length == 4) {
    //         fitPhotos(this.itemElements.length, 2);
    //     } else {
    //         fitPhotos(this.itemElements.length, this.itemElements.length);
    //     }
    //
    //     // console.log("winner", bestColCount, bestSizes);
    //
    //
    //     let totalRows = Math.ceil(this.itemElements.length / bestColCount);
    //     let rowHeight = 1000;
    //
    //     let maxRowWidth = _.max(bestRowWidths);
    //     let scale = Math.min(1, size.width / maxRowWidth);
    //
    //     if (scale * rowHeight * totalRows - (scale * rowHeight * overlap * (totalRows - 1)) > size.height) {
    //         scale = size.height / (rowHeight * totalRows - (rowHeight * overlap * (totalRows - 1)));
    //     }
    //
    //     let contentSizes = _.map(bestSizes, size => size.scale(scale));
    //
    //     rowHeight *= scale;
    //
    //     let itemBounds = [];
    //
    //     let y = size.centerV - rowHeight * totalRows / 2;
    //
    //     for (let row = 0; row < totalRows; row++) {
    //         let items = contentSizes.slice(row * bestColCount, row * bestColCount + bestColCount);
    //         let x = size.centerH - _.sumBy(items, i => i.width) / 2;
    //         for (let i = 0; i < items.length; i++) {
    //             itemBounds.push({
    //                 size: new geom.Rect(x, y, items[i]),
    //                 rotate: random() * maxRotate - maxRotate / 2
    //             });
    //             x += items[i].width - items[i].width * overlap;
    //         }
    //         y += rowHeight - rowHeight * overlap;
    //     }
    //
    //     return itemBounds;
    //
    //     //
    //     // let contentSizes = [];
    //     // let unscaledWidth = 0;
    //     // for (let item of this.itemElements) {
    //     //     let contentSize = item.content.contentSize;
    //     //     contentSize = contentSize.scale(size.height / contentSize.height);
    //     //     contentSizes.push(contentSize);
    //     //     unscaledWidth += contentSize.width;
    //     //     if (this.itemElements.indexOf(item) < this.itemElements.length - 1) {
    //     //         unscaledWidth -= contentSize.width * overlap;
    //     //     }
    //     // }
    //     //
    //     // let scale = Math.min(1, size.width / (unscaledWidth));
    //     // contentSizes = _.map(contentSizes, size => size.scale(scale));
    //     //
    //     // let itemBounds = [];
    //     // let x = size.left;
    //     // let rotate = -(maxRotate / 2);
    //     //
    //     // for (let i = 0; i < this.itemElements.length; i++) {
    //     //     let contentSize = contentSizes[i];
    //     //     let offsetY = i % 2 == 0 ? verticalAltSpacing : 0;
    //     //     itemBounds.push({
    //     //         size: new geom.Rect(x, size.top + offsetY, contentSize),
    //     //         rotate: rotate
    //     //     });
    //     //     x += contentSize.width - contentSize.width * overlap;
    //     //     rotate += maxRotate / (this.itemElements.length - 1);
    //     // }
    //     // return itemBounds;
    //
    // }

    layoutCollage(size) {
        let sizes = _.map(this.itemElements, item => item.contentSize);

        let bestFitLayout;
        let bestFitScore = 10000000;

        for (let w = (size.width / 2); w <= size.width; w += 20) {
            let layout = linearPartition(sizes, {
                containerWidth: w,
                idealElementHeight: size.height / 3,
                spacing: this.gap
            });

            let scale = Math.min(1, size.height / layout.height);

            let scaledWidth = layout.width * scale;
            if (size.width - scaledWidth < bestFitScore) {
                bestFitScore = size.width - scaledWidth;
                bestFitLayout = layout;
            }
        }

        let scale = Math.min(1, size.height / bestFitLayout.height);

        let offsetX = size.width / 2 - bestFitLayout.width * scale / 2;
        let offsetY = size.height / 2 - bestFitLayout.height * scale / 2;

        let itemBounds = _.map(bestFitLayout.positions, item => {
            return new geom.Rect(item.x * scale + offsetX, item.y * scale + offsetY, item.width * scale, item.height * scale);
        });

        this.itemElements.forEach((item, index) => {
            item.calcSize(itemBounds[index].size);
            item.bounds = itemBounds[index].offset(10, 10);
        });

        return new geom.Size(bestFitLayout.width * scale, bestFitLayout.height * scale);
    }

    layoutTiles(props, size) {
        let ratios = _.map(this.itemElements, item => 1 / item.contentSize.aspectRatio);

        let layouts = tileImages(size.width, size.height, ratios);

        this.tileLayouts = layouts;
        let index = parseInt(this.model.gridLayout) || 0;

        if (index >= layouts.length) {
            index = 0;
        }

        let itemBounds = _.map(layouts[index], rect => {
            return new geom.Rect(rect.x, rect.y, rect.w, rect.h).deflate(this.gap / 2, this.gap / 2);
        });

        this.itemElements.forEach((item, index) => {
            let itemProps = props.children.setProps(item, itemBounds[index].size, { fitAsset: true });
            itemProps.bounds = itemBounds[index];
        });

        return size;
    }

    layoutGrid(props, size) {
        let layouter = this.getLayouter(this.itemElements, size);

        let gridLayout = this.model.gridLayout;
        if (!gridLayout && this.type == "TrayPhotoCollage") {
            gridLayout = 1; // this defaults the grid to top/bottom layout when adding the first additional cell to a tray
        }

        layouter.calcGridLayout({
            gridLayout: gridLayout,
            showGutter: this.showGutter,
            gap: this.gap
        });

        return size;
    }

    // endregion

    getCustomSlideColorsLabel() {
        return "Cell Color";
    }

    getCustomBackgroundColorsLabel() {
        return "Gutter";
    }

    getCustomSlideColors() {
        let slideColors = [];
        slideColors.push({
            name: PaletteColorType.BACKGROUND_LIGHT,
            isColor: false,
            color: app.currentTheme.palette.getColor(PaletteColorType.BACKGROUND_LIGHT),
            enabled: true
        });
        slideColors.push({
            name: PaletteColorType.BACKGROUND_DARK,
            isColor: false,
            color: app.currentTheme.palette.getColor(PaletteColorType.BACKGROUND_DARK),
            enabled: true
        });

        _.each(app.currentTheme.palette.getSlideColors(), (color, key) => {
            slideColors.push({
                name: key,
                color: color,
                enabled: true
            });
        });
        slideColors.push({
            name: "colorful",
            enabled: true
        });

        return slideColors;
    }

    _migrate_8() {
        super._migrate_8();
        if (this.model.items && this.model.items.length === 2 && !this.model.gridLayout) {
            this.model.gridLayout = "0";
        }
    }
}

class PhotoCollageItem extends CollectionItemElement {
    static get schema() {
        return {
            cellColor: "auto"
        };
    }

    get selectionPadding() {
        return 0;
    }

    get requireParentSelection() {
        return false;
    }

    get passThroughSelection() {
        return true;
    }

    get canDropImage() {
        return true;
    }

    get canHandlePaste() {
        return true;
    }

    get showDefaultOverlay() {
        return !this.showImage && !this.showText;
    }

    get frameStyle() {
        if (this.model.frame && this.model.frame != "none") {
            return this.model.frame;
        } else {
            return this.parentElement.model.frame || "none";
        }
    }

    get aspectRatio() {
        return this.parentElement.aspectRatio;
    }

    get showImage() {
        return this.model.content_value != null;
    }

    get showText() {
        if (this.parentElement.allowText) {
            return this.model.blocks && this.model.blocks.length > 0;
        } else {
            return false;
        }
    }

    get textPosition() {
        return getValueOrDefault(this.model.textPosition, PositionType.CENTER);
    }

    get listStyle() {
        return this.model.listStyle || "bullets";
    }

    get showFrame() {
        return this.frameStyle != "none";
    }

    get showShadow() {
        return this.parentElement.model.showShadow;
    }

    get captionHeight() {
        return 50;
    }

    get contentSize() {
        let contentSize;
        if (this.showImage) {
            contentSize = this.content.getContentSize();
        } else {
            contentSize = new geom.Size(100, 100);
        }
        // if (this.text && this.text.textPosition == PositionType.BOTTOM) {
        //     contentSize.height = contentSize.height * 1.4;
        // }
        return contentSize;
    }

    _build() {
        if (this.showImage) {
            this.content = this.addElement("contentElement", () => PhotoCollageContent, {
                allowImageScaling: this.aspectRatio != "fit"
            });
            this.content.layer = -1;
        }

        if (this.showText) {
            let textClass;
            if (this.showImage) {
                textClass = PositionableTextElement;
            } else {
                textClass = TextElement;
            }

            this.contentBlocks = this.addElement("contentBlocks", () => ContentBlockContainer, {
                allowedBlockTypes: TextBlockPreset.HEADLINE,
                defaultBlockType: ContentBlockType.HEADLINE,
                autoHeight: true,
                autoWidth: this.model.textWidth == null,
                scaleTextToFit: true,
                selectionPadding: 0,
                canDragPosition: true,
                canDragResize: true
            });
        }
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        // create decoration for cell
        let decorationStyles = {
            type: "frame",
            shape: "rect",
        };

        switch (this.frameStyle) {
            case "light":
                decorationStyles.strokeColor = "white";
                decorationStyles.strokeWidth = 2;
                if (this.parentElement.showGutter || this.parentElement.aspectRatio == "fit") {
                    decorationStyles.shadow = {
                        blur: 14,
                        offsetX: 0,
                        offsetY: 0,
                        opacity: 0.2
                    };
                }
                break;
            case "dark":
                decorationStyles.strokeColor = "background_dark";
                decorationStyles.strokeWidth = 2;
                break;
            default:
        }

        // calculate available content bounds
        let contentBounds = new geom.Rect(0, 0, size);
        if (this.showFrame && this.showImage) {
            contentBounds = contentBounds.deflate(decorationStyles.strokeWidth);
        }

        // calcProps on image
        if (this.showImage) {
            let contentProps = children.setProps(this.content, contentBounds.size, options);
            contentProps.bounds = contentBounds;
        }

        // calcProps on text
        if (this.showText) {
            let contentBlocksProps = children.setProps(this.contentBlocks, size, options);
            contentBlocksProps.bounds = new geom.Rect(0, 0, contentBlocksProps.size);
        }

        // create item decoration (need to do this after calcProps is called on content so we can evaluate whether
        // an image is completely occluding the decoration and then use transparent color so that we don't see
        // anti-aliasing artifacts on the edges

        if (!this.content || this.content.isBackgroundVisible) {
            decorationStyles.fillColor = this.getSlideColor().toRgbString();
        } else {
            decorationStyles.fillColor = "transparent";
        }

        this.createDecoration(decorationStyles);

        return { size };
    }

    getUserDefinedFillColor() {
        if (this.model.cellColor == null || this.model.cellColor == "auto" || this.model.cellColor == "slide") {
            return null;
        } else {
            return app.currentTheme.palette.getColor(this.model.cellColor);
        }
    }

    getBackgroundColor(forElement) {
        if (this.showImage && forElement && this.content && forElement != this.content.assetElement) {
            return BackgroundStyleType.IMAGE;
            //  } else if (this.showFrame) {
            //      return app.currentTheme.palette.getColor(this.frame.styles.fillColor);
        } else {
            return super.getBackgroundColor(forElement);
        }
    }

    _migrate_8() {
        // migrate no set cellColor to use theme instead of backgroundAccent which is default now
        if (!this.model.cellColor) {
            this.model.cellColor = this.canvas.getSlideColor();
        }
    }
}

class PhotoCollageContent extends ContentElement {
    get defaultOverlayType() {
        return "ContentElementDefaultOverlay";
    }

    get _canSelect() {
        return false;
    }

    get _canRollover() {
        return false;
    }
}

export { PhotoCollage, PhotoCollageItem, PhotoCollageContent };

export const elements = {
    PhotoCollage
};
